import { useState, useEffect } from "react";
import { Bell, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { grantPushConsent, isNativePlatform } from "@/services/pushNotificationService";

const DISMISSED_KEY = "notification-prompt-dismissed-at";
// Re-prompt after 7 days if the user said "not now"
const RE_PROMPT_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

const isRecentlyDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < RE_PROMPT_AFTER_MS;
  } catch {
    return false;
  }
};

export const NotificationPermissionPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // NEVER show on native — Apple requires explicit user-initiated consent only
    if (isNativePlatform()) return;

    // Check if Notifications API is available
    if (!('Notification' in window)) return;

    // Don't pester users who already dismissed recently.
    if (isRecentlyDismissed()) return;

    // Show prompt only if permission is 'default' (not asked yet) — web only
    if (Notification.permission === 'default') {
      const timer = setTimeout(() => setShowPrompt(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // ignore storage failures (Safari private mode etc.)
    }
    setShowPrompt(false);
  };

  const handleRequestPermission = async () => {
    if (!user?.id) return;
    setIsRequesting(true);
    try {
      // Must go through grantPushConsent — it asks the OS, applies the
      // post-consent category defaults (once ever), and registers the device.
      // Calling Notification.requestPermission() + initializeWebPush() directly
      // skipped the category step, so a web user who accepted this prompt got
      // permission and a registered device with all nine categories still false:
      // consent given, zero notifications delivered. See App Store 4.5.4 work in
      // supabase/migrations/20260816120000_push_opt_in_default.sql.
      const success = await grantPushConsent(user.id);

      if (success) {
        new Notification('התראות הופעלו! 🎉', {
          body: 'תקבלו עדכונים על הודעות חדשות ואירועים',
          icon: '/favicon.ico',
          dir: 'rtl',
          lang: 'he'
        });
      }

      setShowPrompt(false);
    } catch (error) {
      console.error('Error requesting permission:', error);
    } finally {
      setIsRequesting(false);
    }
  };

  if (!showPrompt) return null;

  return (
    // In the page flow under the header, like SubscriptionBanner. As a floating overlay above
    // the bottom nav it covered whatever sat at the end of the page (e.g. a comment box's
    // send button), and content at the very bottom could not be scrolled clear of it.
    <div className="px-4 pt-2" dir="rtl">
      <div
        role="region"
        aria-label="הפעלת התראות"
        className="md:w-80 bg-card border border-border rounded-xl shadow-sm p-3 animate-in fade-in slide-in-from-top-2"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 text-primary rounded-lg shrink-0">
            <Bell className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground leading-tight">הפעלת התראות</p>
            <p className="text-xs text-muted-foreground leading-tight mt-0.5">קבלו עדכונים על הודעות חדשות</p>
          </div>
          <button
            onClick={handleRequestPermission}
            disabled={isRequesting}
            className="shrink-0 inline-flex min-h-[44px] items-center px-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold transition-transform duration-100 active:scale-[0.96] disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          >
            {isRequesting ? '...' : 'הפעלה'}
          </button>
          <button
            onClick={dismiss}
            aria-label="סגירה"
            className="shrink-0 inline-flex min-h-[44px] min-w-[44px] items-center justify-center text-muted-foreground transition-transform duration-100 active:scale-[0.95] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 rounded-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
